import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/client';
import {
  PhoneIcon, EnvelopeIcon, BuildingOfficeIcon, IdentificationIcon,
  MagnifyingGlassIcon, PlusIcon, XMarkIcon, PencilSquareIcon, TrashIcon,
} from '@heroicons/react/24/outline';

// Business contacts rolodex. Director + admin only. Tracks the people
// outside BBC the team needs to reach — vendors, licensing, legal,
// banking, IT, etc. Name + company + phone + email + industry per the
// director's spec.

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industryFilter, setIndustryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // contact obj, or {} for new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industryFilter !== 'all') params.set('industry', industryFilter);
      if (search) params.set('search', search);
      const { data } = await api.get(`/business-contacts?${params}`);
      setContacts(data);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [industryFilter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/business-contacts/industries').then(({ data }) => setIndustries(data)).catch(() => {});
  }, []);

  const onSave = async (form) => {
    try {
      if (editing && editing.id) {
        await api.patch(`/business-contacts/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/business-contacts', form);
        toast.success('Contact added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/business-contacts/${id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  // Group by industry for display
  const grouped = {};
  for (const c of contacts) {
    const key = c.industry || 'Other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  }
  const groupKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="animate-slide-up max-w-5xl mx-auto space-y-5">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <IdentificationIcon className="w-6 h-6" /> Business Contacts
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Vendors, licensing contacts, legal, banking, IT — everyone outside BBC the team needs to reach.
          </p>
        </div>
        <button onClick={() => setEditing({})}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-xl">
          <PlusIcon className="w-4 h-4" /> Add Contact
        </button>
      </header>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, email, phone…"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-orange-500 focus:outline-none" />
        </div>
        <select value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 px-3 py-1.5 bg-white">
          <option value="all">All industries</option>
          {industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      {/* List grouped by industry */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : contacts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <BuildingOfficeIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No contacts yet.</p>
          <p className="text-sm text-gray-500 mt-1">Click "Add Contact" to add your first one.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupKeys.map(industry => (
            <div key={industry} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {industry} <span className="text-gray-400 font-normal">· {grouped[industry].length}</span>
                </p>
              </div>
              <ul className="divide-y divide-gray-100">
                {grouped[industry].map(c => (
                  <li key={c.id} className="px-5 py-3 hover:bg-gray-50/50 group">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                          {c.company && (
                            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                              <BuildingOfficeIcon className="w-3 h-3" /> {c.company}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-3 text-xs">
                          {c.phone && (
                            <a href={`tel:${c.phone}`}
                              className="inline-flex items-center gap-1 text-gray-700 hover:text-orange-600">
                              <PhoneIcon className="w-3.5 h-3.5" /> {c.phone}
                            </a>
                          )}
                          {c.email && (
                            <a href={`mailto:${c.email}`}
                              className="inline-flex items-center gap-1 text-gray-700 hover:text-orange-600">
                              <EnvelopeIcon className="w-3.5 h-3.5" /> {c.email}
                            </a>
                          )}
                        </div>
                        {c.notes && (
                          <p className="text-xs text-gray-500 mt-1.5">{c.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button onClick={() => setEditing(c)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700">
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDelete(c.id)}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ContactModal
          contact={editing}
          industries={industries}
          onSave={onSave}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ContactModal({ contact, industries, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: contact.name || '',
    company: contact.company || '',
    phone: contact.phone || '',
    email: contact.email || '',
    industry: contact.industry || '',
    notes: contact.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const isNew = !contact.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required.');
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {isNew ? 'Add contact' : 'Edit contact'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name" required>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Company">
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(317) 555-1234"
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
          </div>
          <Field label="Industry">
            <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              <option value="">— Pick one —</option>
              {industries.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} placeholder="Anything worth remembering — best time to call, contract dates, etc."
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y" />
          </Field>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Add Contact' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
